import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Favoritable, FavoritableType } from '@/types';

// This is like a list of what information we want to remember about each favorite item
interface FavoriteItem {
  id: string;
  title: string;
  imageUrl: string;
  type: 'sequence' | 'display';  // Is it a light sequence or a display?
  addedAt: string;
}

// This is our Favorites toy box and what we can do with it
interface FavoritesStore {
  // List of favorited items (both sequences and displays)
  favorites: Favoritable[];
  
  // Add an item to favorites
  addToFavorites: (item: Favoritable) => void;
  
  // Remove an item from favorites
  removeFromFavorites: (id: string) => void;
  
  // Check if an item is in favorites
  isFavorite: (id: string) => boolean;
  
  // Get favorites by type
  getFavoritesByType: (type: FavoritableType) => Favoritable[];
}

// Create our Favorites toy box! 🎁
export const useFavoritesStore = create<FavoritesStore>()(
  persist(
    (set, get) => ({
      // Start with an empty list
      favorites: [],

      // Function to add a new favorite
      addToFavorites: (item) => 
        set((state) => {
          // Don't add if it's already there
          if (state.favorites.some(fav => fav.id === item.id)) {
            return state;
          }
          
          // Add the new favorite with current date
          return {
            favorites: [
              ...state.favorites,
              item
            ]
          };
        }),

      // Function to remove a favorite
      removeFromFavorites: (id) =>
        set((state) => ({
          favorites: state.favorites.filter(item => item.id !== id)
        })),

      // Function to check if something is a favorite
      isFavorite: (id) => 
        get().favorites.some(item => item.id === id),

      // Function to get favorites by type
      getFavoritesByType: (type) =>
        get().favorites.filter(item => item.type === type),
    }),
    {
      name: 'favorites-storage', // Name for localStorage
    }
  )
); 